import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { Interpolation } from '../../config/i18n/components/Interpolation';
import { ButtonBox, DangerButton, TertiaryButton } from '../components/Buttons';
import { SlotService } from '../services/SlotService';

type Props = {
  slotId: number;
  slotName: string;
  onClosed: (slotId: number | undefined) => void;
};

export const DeleteSlotFlow = ({ slotId, slotName, onClosed }: Props) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [slotService] = useState<SlotService>(new SlotService());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      slotService.cancelAllRequests();
    };
  }, [slotService]);

  const [name, nameComponent] = useFormItem({
    type: InputType.TYPE_TEXT,
    label: t('deleteSlot.name.input'),
    value: '',
    validator: null,
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('deleteSlot.name.focused'),
      showRequiredIndicator: true,
    },
  });

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await slotService.deleteSlot(slotId);
      setLoading(false);
      onClosed(slotId);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  // Using a form validator results in the input being marked as invalid
  // while the user is typing. We manually check if the typed value equals
  // the slot name to enable the submit button
  const canSubmit = !loading && name === slotName;

  return (
    <Dialog title={t('deleteSlot.title')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          <Interpolation i18nKey="deleteSlot.content" items={[{ name: 'color_gray', color: 'grey.700' }]} values={{ slotName }} />
          {nameComponent}
          <ButtonBox>
            <TertiaryButton disabled={loading} onClick={() => onClosed(undefined)}>
              {t('deleteSlot.back')}
            </TertiaryButton>
            <DangerButton disabled={!canSubmit} type="submit">
              {t('deleteSlot.submit')}
            </DangerButton>
          </ButtonBox>
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
